import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import VideoCard from '../../components/VideoCard';

import { categoryService } from '../../services/categoryService';

import useContentStore from '../../store/contentStore';

const CategoryGallery = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Use centralized content store
  const { 
    contentItems, 
    fetchContent, 
    subscribeToChanges, 
    unsubscribeFromChanges 
  } = useContentStore();

  useEffect(() => {
    loadData();
    
    // Subscribe to real-time updates
    subscribeToChanges();
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribeFromChanges();
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoryData] = await Promise.all([
        categoryService?.getAll(),
        fetchContent() // Fetch from centralized store
      ]);
      setCategories(categoryData || []);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedContent = contentItems
    ?.filter(item => {
      const categoryMatch = selectedCategory === 'all' || item?.categoryId === selectedCategory;
      const typeMatch = filterType === 'all' || item?.type === filterType;
      return categoryMatch && typeMatch;
    })
    ?.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b?.uploadDate) - new Date(a?.uploadDate);
      if (sortBy === 'oldest') return new Date(a?.uploadDate) - new Date(b?.uploadDate);
      if (sortBy === 'rating') return (b?.rating || 0) - (a?.rating || 0);
      if (sortBy === 'price-low') return (a?.price || 0) - (b?.price || 0);
      if (sortBy === 'price-high') return (b?.price || 0) - (a?.price || 0);
      return 0;
    });

  const handleWhatsAppRequest = (content) => {
    const message = `Olá UP MIDIA! 👋\n\nGostaria de solicitar informações sobre:\n\n📌 ${content?.title}\n🎨 Categoria: ${content?.category}\n⭐ Avaliação: ${content?.rating} estrelas\n\nAguardo retorno!\n\n*Enviado via UP MIDIA Gallery*`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5511999999999?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-glow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/')}
                className="transition-colors hover:bg-primary/5"
              >
                Voltar para Início
              </Button>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-primary">
                Galeria de Categorias
              </h1>
            </div>
          </div>
        </div>
      </header>
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando galeria...</p>
          </div>
        </div>
      ) : (
        <main className="pt-8 px-4 md:px-6 lg:px-10 pb-12">
          <div className="max-w-7xl mx-auto">
            {/* Filters */}
            <div className="mb-8 space-y-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Icon name="Layers" size={16} color="var(--color-primary)" />
                  Categoria
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === 'all' ?'bg-primary text-white shadow-md' :'bg-card text-foreground hover:bg-primary/10'
                    }`}
                  >
                    Todas
                  </button>
                  {categories?.map((category) => (
                    <button
                      key={category?.id}
                      onClick={() => setSelectedCategory(category?.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category?.id
                          ? 'bg-primary text-white shadow-md' :'bg-card text-foreground hover:bg-primary/10'
                      }`}
                    >
                      {category?.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Type and Sort Filters */}
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tipo de Conteúdo
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg bg-card border border-border text-foreground"
                  >
                    <option value="all">Todos</option>
                    <option value="art">Artes</option>
                    <option value="video">Vídeos</option>
                  </select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ordenar por
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg bg-card border border-border text-foreground"
                  >
                    <option value="newest">Mais Recentes</option>
                    <option value="oldest">Mais Antigos</option>
                    <option value="rating">Melhor Avaliados</option>
                    <option value="price-low">Menor Preço</option>
                    <option value="price-high">Maior Preço</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Content Grid - Using Shared VideoCard Component */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAndSortedContent?.map((item) => (
                <VideoCard
                  key={item?.id}
                  content={item}
                  onWhatsAppRequest={handleWhatsAppRequest}
                  onContentClick={() => {}}
                  onRatingChange={() => {}}
                  variant="category"
                />
              ))}
            </div>

            {filteredAndSortedContent?.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                  Nenhum conteúdo encontrado
                </h3>
                <p className="text-muted-foreground">
                  Tente ajustar os filtros
                </p>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
};

export default CategoryGallery;